import { Card } from '@acceso/design-system';
import clsx from 'clsx';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { GeneralHeader } from '@/components/HeaderPage/HeaderPage';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useSegmentTextOnScreenPermissions } from '@/features/broadcast-schedule-program/hooks/useSegmentTextOnScreenPermissions';
import { useDeleteSegmentOcr } from '@/features/segments/api/delete-segment-ocr';
import { TSegmentOcr } from '@/features/segments/types';
import { convertISOToSeconds } from '@/utils/hh-mm-ss-into-number';

import { ESegmentToolkit } from '../../types';
import { BroadcastScheduleProgramBodySegmentToolkitNavigation } from '../BroadcastScheduleProgramBodySegmentToolkitNavigation';

import { BroadcastScheduleProgramBodySegmentToolkitRotuloForm } from './components/BroadcastScheduleProgramBodySegmentToolkitRotuloForm';
import { BroadcastScheduleProgramBodySegmentToolkitRotuloTable } from './components/BroadcastScheduleProgramBodySegmentToolkitRotuloTable';

export default function BroadcastScheduleProgramBodySegmentToolkitRotulo() {
  const {
    formBox,
    activeSegment,
    rotuloData,
    videoControlRef,
    handleTooltikClick,
    setFormBox,
    setActiveSegment,
    setRotuloData,
    setSegmentItems,
  } = useBroadcastScheduleProgram();
  const { mutateAsync: deleteOcrSegment } = useDeleteSegmentOcr({});
  const { t } = useTranslation();

  const handlePreviewBox = (box: TSegmentOcr) => {
    if (!activeSegment) return;

    const seconds = convertISOToSeconds(box.start_datetime, new Date(activeSegment.start_datetime));

    videoControlRef.current?.seekToSecond(seconds || 0);
  };

  const { canCreate, canUpdate, canDelete } = useSegmentTextOnScreenPermissions();

  const handleEdit = useCallback(
    (item: TSegmentOcr) => {
      if (!canUpdate) return;
      setFormBox('rotulo');
      setRotuloData(item);
    },
    [setFormBox, canUpdate]
  );

  const handleCreate = useCallback(() => {
    if (!canCreate) return;
    setFormBox('rotulo');
  }, [setFormBox, canCreate]);

  const handleDelete = useCallback(
    (item: TSegmentOcr) => {
      if (!canDelete) return;
      deleteOcrSegment(item.id || '').then(() => {
        setActiveSegment(prev => {
          if (prev) {
            return {
              ...prev,
              segment_ocrs: prev.segment_ocrs?.filter(logo => logo.id !== item.id),
            };
          }
          return prev;
        });
        setSegmentItems(prev => {
          if (!prev) return prev;
          return prev.map(segment => {
            if (segment.id === activeSegment?.id) {
              return {
                ...segment,
                segment_ocrs: segment.segment_ocrs?.filter(ocr => ocr.id !== item.id),
              };
            }
            return segment;
          });
        });
      });
    },
    [canDelete]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLocaleLowerCase() === 'i') {
        event.preventDefault();
        handleCreate();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCreate]);

  return (
    <Card
      classes={{
        rootSection: clsx('w-full flex-auto order-3 lg:order-2'),
        container: 'py-size-4',
      }}
    >
      <div className="flex w-full flex-col gap-2">
        {formBox !== 'rotulo' ? (
          <>
            <GeneralHeader
              name={t('Rótulos')}
              buttonName={canCreate ? t('Añadir') : undefined}
              onClick={handleCreate}
              onClose={() => handleTooltikClick(ESegmentToolkit.ROTULOS)}
            />
            <BroadcastScheduleProgramBodySegmentToolkitRotuloTable
              items={activeSegment?.segment_ocrs || []}
              onPreview={handlePreviewBox}
              onUpdate={handleEdit}
              onDelete={handleDelete}
            />
          </>
        ) : (
          <div className="flex flex-col gap-2">
            <BroadcastScheduleProgramBodySegmentToolkitRotuloForm />
            <BroadcastScheduleProgramBodySegmentToolkitNavigation
              items={activeSegment?.segment_ocrs || []}
              activeItem={rotuloData}
              onChangeData={setRotuloData}
            />
          </div>
        )}
      </div>
    </Card>
  );
}
